import { Popover, ContextMenu } from "radix-ui";
import { SideBarMenuButton } from "../../../../ProjectFilter/SideBarMenu/SideBarMenuButton";
import ArchiveIcon from "../../../../atom/icons/ArchiveIcon";
import { ScrollArea } from "../../../../atom/ScrollArea";
import SpaceDot from "../../../../atom/Space/SpaceDot";
import { juneTrack } from "../../../../../utils/june/analytics";
import { JUNE_EVENT, JUNE_LOCATION } from "../../../../../hooks/june/juneEvent";
import { useMarkSpaceUndoneMutation } from "../../../../../react-query/space/status/useMarkSpaceUndoneMutation";
import { useMoveToTrashSpaceMutation } from "../../../../../react-query/space/deletion/useMoveToTrashSpaceMutation";
import { SpaceType } from "../../../../../types/space";
import { useFetchArchivedSpaceQuery } from "../../../../../react-query/space/core/useFetchSpaceQuery";
import { useUpdateArchivedSpaceMutation } from "../../../../../react-query/space/core/useUpdateArchivedSpaceMutation";
import { useState } from "react";
import clsx from "clsx";
import ModalPanel from "../Common";

import { ContextMenuContent } from "../../../../atom/ContextMenuContent";

const ArchivedSpacesPopover = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { data: archivedSpace } = useFetchArchivedSpaceQuery();
  const { mutate: revertSpace } = useMarkSpaceUndoneMutation();
  const { mutate: trashSpace } = useMoveToTrashSpaceMutation();
  const { mutate: updateArchivedSpace } = useUpdateArchivedSpaceMutation();

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    open && juneTrack(JUNE_EVENT.VIEW_ARCHIVED_SPACE);
  };

  const handleUnArchiveSpace = (space: SpaceType) => {
    revertSpace(space.id);
    juneTrack(JUNE_EVENT.RESTORE_SPACE, { type: "completed_items" });
  };

  const handleSpaceDelete = (space: SpaceType) => {
    trashSpace(space.id, {
      onSuccess: () => {
        juneTrack(JUNE_EVENT.DELETE_SPACE, { location: JUNE_LOCATION.ARCHIVED_SPACE });
      },
    });
  };

  const handleChangeColor = (space: SpaceType, colorValue: string) => {
    updateArchivedSpace(
      {
        id: space.id,
        payload: { color: colorValue },
      },
      {
        onSuccess: () => {
          juneTrack(JUNE_EVENT.UPDATE_SPACE, {
            type: "color",
            location: JUNE_LOCATION.ARCHIVED_SPACE,
          });
        },
      }
    );
  };

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      modal={true}
    >
      <Popover.Trigger asChild>
        <SideBarMenuButton
          className={isOpen ? "bg-w-8-hover" : ""}
          tooltipLabel={"Archived spaces"}
        >
          <ArchiveIcon color={isOpen ? "#69E4FF" : "#CACDCE"} />
        </SideBarMenuButton>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          alignOffset={-9}
          side="right"
          sideOffset={83}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className={clsx(
            "z-[100] h-[520px] w-[300px] rounded-[7px] border border-solid border-25 bg-20 focus:outline-none",
            "origin-bottom-left data-[state=closed]:animate-popup-scale-out data-[state=open]:animate-popup-scale-in"
          )}
        >
          <ModalPanel.Header>
            <ModalPanel.Title>Archived spaces</ModalPanel.Title>
          </ModalPanel.Header>
          {archivedSpace && archivedSpace.length > 0 && (
            <ScrollArea
              height={470}
              showScrollBar={true}
            >
              <ul className="flex flex-col gap-[8px] py-[4px] pl-[8px]">
                {archivedSpace.map((space) => (
                  <ContextMenu.Root key={space.id}>
                    <ContextMenu.Trigger asChild>
                      <li
                        className={
                          "flex h-9 w-full select-none items-center gap-1.5 rounded-[5px] px-2 text-[14px] leading-[150%] text-white hover:bg-w-8-hover data-[state=open]:bg-w-8-hover"
                        }
                      >
                        <SpaceDot
                          color={space.color}
                          className="flex-shrink-0"
                        />
                        <div className="flex-grow overflow-hidden text-ellipsis whitespace-nowrap">
                          {space.title}
                        </div>
                      </li>
                    </ContextMenu.Trigger>
                    <ContextMenuContent>
                      <ContextMenuContent.ColorPicker
                        spaceColor={space.color}
                        onChangeSpaceColor={(color) => handleChangeColor(space, color)}
                      />
                      <ContextMenuContent.Item onClick={() => handleUnArchiveSpace(space)}>
                        Unarchive
                      </ContextMenuContent.Item>
                      <ContextMenuContent.Item onClick={() => handleSpaceDelete(space)}>
                        Delete
                      </ContextMenuContent.Item>
                    </ContextMenuContent>
                  </ContextMenu.Root>
                ))}
              </ul>
            </ScrollArea>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default ArchivedSpacesPopover;
